import { memo, useState, useCallback } from "react";
import { Typography, Box, CircularProgress, IconButton } from "@mui/material";
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails, Grid2 } from "@mui/material";
import { icon } from "@/app/style/icons";
import MarkdownRenderer from "@/domains/core/components/MarkdownRenderer";
import { useDocumentSummary } from "@documents/hooks/useDocumentSummary";
import { Document } from "@documents/hooks/useDocumentsList";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useNavigate } from "react-router-dom";
import { RouteTo } from "@routing/RouteMap";
import { isDocumentVisible } from "@/utils/documentDisplay";

interface LawSelectionCardProps {
    documents: Document[];
    selectionState: Record<string, boolean>;
    onToggle: (documentId: string, newState: boolean) => void;
    showOriginals: boolean;
    showAmendments: boolean;
}

interface DocumentSelectionState {
    [documentId: string]: boolean;
}

/**
 * Dokument-Akkordeon-Komponente (optimiert mit memo)
 * Zeigt ein einzelnes Dokument mit Auswahlmöglichkeit an
 */
const DocumentAccordion = memo(({ 
    document, 
    isSelected,
    toggleLoading,
    onToggle,
    excluded,
}: { 
    document: Document; 
    isSelected: boolean;
    toggleLoading: boolean;
    onToggle: (documentId: string, newState: boolean) => void;
    excluded: boolean;
}) => {
    const [expanded, setExpanded] = useState(false);
    const { summary, loading: summaryLoading, error } = useDocumentSummary(document.id, !expanded);
    const navigate = useNavigate();

    const handleAccordionToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(prev => !prev);
    }, []);

    // Optimierter Toggle-Handler
    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(document.id, !isSelected);
    }, [document.id, isSelected, onToggle]);

    // Add click handler for lexicon navigation
    const handleOpenInLexicon = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`${RouteTo.LEXIKON}?documentId=${document.id}`);
    }, [document.id, navigate]);
    
    return (
        <MuiAccordion 
            key={document.id} 
            sx={{ background: "solid", boxShadow: "none", mb: 1 }}
            expanded={expanded}
        >
            <AccordionSummary
                expandIcon={<icon.dropdown sx={{ fontSize: "1.5rem" }} />}
                onClick={handleAccordionToggle}
            >
                <Grid2 container alignItems={"center"} spacing={1}>
                    <Grid2>
                        <IconButton 
                            onClick={handleToggle}
                            size="small"
                            sx={{ mr: 1, mt: 0.05 }}
                            disabled={toggleLoading}
                        >
                            {toggleLoading ? (
                                <CircularProgress size={20} />
                            ) : isSelected ? (
                                <CheckBoxIcon />
                            ) : (
                                <CheckBoxOutlineBlankIcon />
                            )}
                        </IconButton>
                    </Grid2>
                    
                    <Grid2 flex={1}>
                        <Grid2 container spacing={1} alignItems={"baseline"}>
                            <Grid2>
                                <Typography fontSize={"0.98rem"} sx={{ textDecoration: excluded ? 'line-through' : 'none', color: excluded ? 'text.disabled' : 'inherit' }}>
                                    {document.reference 
                                        ? document.reference.replace(/_/g, " ")
                                        : "Unbenanntes Dokument"
                                    }
                                </Typography>
                            </Grid2>

                            {document.hoverTitle && (
                                <Grid2>
                                    <Typography fontSize={"0.75rem"} color={"textDisabled"}>
                                        {document.hoverTitle}
                                    </Typography>
                                </Grid2>
                            )}

                            <Grid2>
                                <IconButton 
                                    size="small" 
                                    onClick={handleOpenInLexicon}
                                    sx={{ ml: 1, color: "text.secondary" }}
                                    title="Im Lexikon öffnen"
                                >
                                    <icon.openInNew />
                                </IconButton>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </AccordionSummary>
            
            <AccordionDetails>
                {summaryLoading ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Typography variant="body2" color="error" sx={{ mx: 2, my: 1 }}>
                        Fehler beim Laden der Zusammenfassung
                    </Typography>
                ) : summary?.text ? (
                    <Box sx={{ mx: 2, my: 1 }}>
                        <MarkdownRenderer>
                            {summary.text}
                        </MarkdownRenderer>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mx: 2, my: 1 }}>
                        Keine Zusammenfassung verfügbar
                    </Typography>
                )}
            </AccordionDetails>
        </MuiAccordion>
    );
}, (prevProps, nextProps) => {
    // Benutzerdefinierte Vergleichsfunktion zur Verhinderung unnötiger Neuberechnungen
    return prevProps.isSelected === nextProps.isSelected &&
        prevProps.toggleLoading === nextProps.toggleLoading &&
        prevProps.document.id === nextProps.document.id &&
        prevProps.excluded === nextProps.excluded;
});

/**
 * Hauptkomponente für die Gesetzesauswahl 
 * Zeigt eine Kategorie von Dokumenten mit Auswahlmöglichkeit an
 */
const LawSelectionCard = memo(({ documents, selectionState, onToggle, showOriginals, showAmendments }: LawSelectionCardProps) => {
    // Render document accordions based on parent-managed state
    const documentList = documents.map(document => {
        const excluded = !isDocumentVisible(document, { showOriginals, showAmendments });
        return (
            <DocumentAccordion
                key={document.id}
                document={document}
                isSelected={selectionState[document.id] || false}
                toggleLoading={false}
                onToggle={onToggle}
                excluded={excluded}
            />
        );
    });

    return (
        <Box>
            {documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Keine Dokumente in dieser Kategorie
                </Typography>
            ) : (
                documentList
            )}
        </Box>
    );
});

export default LawSelectionCard;
